import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../lib/api.js';
import './deployments.css';

interface Step {
  id: string;
  idx: number;
  template: string;
  status: string;
  exitCode: number | null;
}

interface DeploymentDetail {
  id: string;
  stackId: string;
  environmentId: string;
  kind: string;
  gitRef: string;
  gitSha: string;
  ranMigrations: boolean;
  status: string;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  steps: Step[];
}

interface LogLine {
  seq: number;
  stepId: string | null;
  stream: 'out' | 'err' | 'system';
  line: string;
}

const LIVE_STATUSES = ['queued', 'running', 'cancelling'];

const STEP_ICON: Record<string, string> = {
  pending: '○',
  running: '◐',
  succeeded: '●',
  failed: '✕',
  skipped: '◌',
  cancelled: '◌',
};

export function DeploymentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data: deployment } = useQuery<DeploymentDetail>({
    queryKey: ['deployments', id],
    queryFn: () => apiFetch(`/deployments/${id}`),
    refetchInterval: (q) => (q.state.data && LIVE_STATUSES.includes(q.state.data.status) ? 3000 : false),
  });

  const [lines, setLines] = useState<LogLine[]>([]);
  const [autoScroll, setAutoScroll] = useState(true);
  const logRef = useRef<HTMLDivElement>(null);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!id) return;
    const es = new EventSource(`/api/v1/deployments/${id}/logs/stream`);
    esRef.current = es;
    es.onmessage = (event) => {
      const data = JSON.parse(event.data) as { stepId: string | null; stream: LogLine['stream']; line: string };
      setLines((prev) => [...prev, { seq: Number(event.lastEventId), ...data, line: data.line }]);
    };
    es.addEventListener('done', () => {
      es.close();
      void queryClient.invalidateQueries({ queryKey: ['deployments', id] });
    });
    es.onerror = () => {
      /* EventSource auto-reconnects with Last-Event-ID */
    };
    return () => es.close();
  }, [id, queryClient]);

  useEffect(() => {
    if (autoScroll && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [lines, autoScroll]);

  const cancel = useMutation({
    mutationFn: () => apiFetch(`/deployments/${id}/cancel`, { method: 'POST' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['deployments', id] }),
  });

  if (!deployment) return <p className="mono dim">loading…</p>;
  const live = LIVE_STATUSES.includes(deployment.status);

  return (
    <div className="deploy-detail">
      <header className="page-header">
        <div>
          <p className="page-eyebrow">Deployment</p>
          <h1>
            <span className="mono">{deployment.gitSha.slice(0, 8)}</span>
          </h1>
          <p className="mono dim" style={{ margin: '4px 0 0' }}>
            {deployment.gitRef} · {deployment.kind}
            {deployment.ranMigrations ? ' · migrations' : ''} ·{' '}
            {new Date(deployment.createdAt).toLocaleString()}
          </p>
        </div>
        <div className="page-actions">
          <span className={`deploy-status deploy-status-${deployment.status}`}>{deployment.status}</span>
          {live ? (
            <button className="btn btn-ghost" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
              Cancel
            </button>
          ) : null}
          <Link className="btn btn-ghost" to={`/stacks/${deployment.stackId}`}>
            Stack
          </Link>
        </div>
      </header>
      {deployment.error ? <p className="error-text">{deployment.error}</p> : null}

      <div className="deploy-layout">
        <aside className="card step-rail">
          <h3>Steps</h3>
          {deployment.steps.length === 0 ? <p className="dim">waiting for worker…</p> : null}
          {deployment.steps.map((s) => (
            <div key={s.id} className={`step-item step-${s.status}`}>
              <span className="step-icon">{STEP_ICON[s.status] ?? '○'}</span>
              <span className="mono">{s.template}</span>
              {s.exitCode !== null && s.exitCode !== 0 ? <span className="mono step-exit">exit {s.exitCode}</span> : null}
            </div>
          ))}
        </aside>

        <div className="card log-pane">
          <div className="log-toolbar">
            <span className="mono dim">{lines.length} lines</span>
            <label className="check-row" style={{ fontSize: 12 }}>
              <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} />
              follow
            </label>
          </div>
          <div className="log-scroll mono" ref={logRef}>
            {lines.map((l) => (
              <div key={l.seq} className={`log-line log-${l.stream}`}>
                {l.line}
              </div>
            ))}
            {live && lines.length === 0 ? <div className="log-line log-system">waiting for output…</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
